import { cn } from '@echo/ui-web';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Figma `global/empty` on the web: a white 24px-radius frame
 * with an illustration, a centred title, a paragraph, and a right-aligned button group on a
 * lighter band along the bottom. Help & Support, "Notifications are off", offline, not found
 * and the camera/photo guidance all share it.
 */
export function EmptyStateFrame({
  art,
  title,
  children,
  actions,
  className,
}: {
  art: ReactNode;
  title: ReactNode;
  children: ReactNode;
  actions: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'flex w-full flex-col items-center overflow-hidden rounded-xxl bg-surface pt-section-lg',
        className,
      )}
    >
      <div className="flex w-full flex-col items-center gap-content-lg px-content-sm sm:px-section-lg">
        {art}
        <div className="flex w-full flex-col gap-content-sm">
          <h1 className="text-center text-title-lg">{title}</h1>
          <div className="text-body-lg text-on-surface-medium">{children}</div>
        </div>
      </div>
      <div className="mt-content-lg flex w-full flex-wrap items-center justify-end gap-content-sm bg-surface/60 px-content-sm pt-content-lg pb-section-sm">
        {actions}
      </div>
    </section>
  );
}

/** Figma `toolbar/desktop`: the "← Back" link above a frame. Rendered as the element the caller passes. */
export function ToolbarBack({
  render,
}: {
  render: (props: { className: string; children: ReactNode }) => ReactNode;
}) {
  const { t } = useLingui();
  return (
    <div className="flex w-full items-center pb-internal-md">
      {render({
        className:
          'inline-flex min-h-tap-target items-center gap-internal-md rounded-sm px-content-sm py-internal-lg font-brand text-label-md text-on-action-secondary hover:bg-action-secondary-focus focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight',
        children: (
          <>
            <ArrowLeft size={24} aria-hidden />
            {t`Back`}
          </>
        ),
      })}
    </div>
  );
}

/** The illustration slot: the mascot at the Figma's 335px, never wider than the frame. */
export function EmptyStateArt({
  src,
  alt,
  ...size
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
}) {
  return (
    <img
      src={src}
      alt={alt}
      width={size.width}
      height={size.height}
      className="h-[287px] w-auto max-w-full object-contain"
    />
  );
}
